/**
 * Operator admin web — operator-facing ops/observability surfaces.
 *
 * Five tabs today:
 *   1. Recently-signed certs (post-publish readback per plan §3.13)
 *   2. Pending keypairs (queue of accountants awaiting `dovach-op sign`)
 *   3. Alerts (chain-broken / agent-offline / provider-down)
 *   4. Audit log search (cross-account, with chain-verified badge + CSV export)
 *   5. Health (live API + Oracle + OCI status)
 *
 * Wave 1 added all the backend routes; this app wraps them in operator-friendly
 * panels. Plan §3.7 calls out account-impersonation and a real operator-login
 * flow as separate features — those land in a follow-up because they need new
 * is_operator column + impersonation token plumbing.
 */

import { ActionIcon, AppShell, Container, Group, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";

import { api, authStore } from "./lib/api";
import { AccountsPanel } from "./screens/AccountsPanel";
import { AlertsPanel } from "./screens/AlertsPanel";
import { AuditSearchPanel } from "./screens/AuditSearchPanel";
import { CertsReadbackPanel } from "./screens/CertsReadbackPanel";
import { ExpiringCertsPanel } from "./screens/ExpiringCertsPanel";
import { HealthPanel } from "./screens/HealthPanel";
import { LoginScreen } from "./screens/LoginScreen";
import { PendingKeypairsPanel } from "./screens/PendingKeypairsPanel";
import { SignupsPanel } from "./screens/SignupsPanel";
import { TopBar, type Tab } from "./screens/TopBar";

export function App() {
  const [authed, setAuthed] = useState<boolean>(() => !!authStore.get());
  // Default to "signups" — the queue you'll most often want to triage.
  const [tab, setTab] = useState<Tab>("signups");
  const [me, setMe] = useState<string>("");

  // Listen for the api-client's "session expired" signal — any 401 from
  // any admin call clears the token and bounces us to login.
  useEffect(() => {
    const onExpired = () => setAuthed(false);
    window.addEventListener("admin-auth-expired", onExpired);
    return () => window.removeEventListener("admin-auth-expired", onExpired);
  }, []);

  // After login (or on first render with a stored token), confirm the
  // session is real. If /auth/me 401s the listener above flips us back.
  useEffect(() => {
    if (!authed) return;
    api.me().then((m) => setMe(m.email)).catch(() => {});
  }, [authed]);

  if (!authed) {
    return <LoginScreen onLoggedIn={() => setAuthed(true)} />;
  }

  const signOut = async () => {
    await api.logout();
    authStore.clear();
    setAuthed(false);
  };

  return (
    <AppShell padding="md" header={{ height: 56 }}>
      <TopBar tab={tab} onTabChange={setTab} />
      <AppShell.Main>
        <Container size="lg">
          <Group justify="space-between" mb="md">
            <Text size="sm" c="dimmed">{me ? `Signed in as ${me}` : ""}</Text>
            <ActionIcon variant="subtle" onClick={signOut} title="Sign out" aria-label="Sign out">
              ⎋
            </ActionIcon>
          </Group>
          <Stack gap="lg">
            {tab === "signups" && <SignupsPanel />}
            {tab === "accounts" && <AccountsPanel />}
            {tab === "certs" && <CertsReadbackPanel />}
            {tab === "expiring" && <ExpiringCertsPanel />}
            {tab === "pending" && <PendingKeypairsPanel />}
            {tab === "alerts" && <AlertsPanel />}
            {tab === "audit" && <AuditSearchPanel />}
            {tab === "health" && <HealthPanel />}
          </Stack>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
